import { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Notification } from '@rio-cloud/rio-uikit';
import moment from 'moment';

import {
  getChallengeDriverPerAccount
} from 'src/services/gamefication';
import { useCoinsState } from 'src/store/coins/coinsSlice';
import { setTotalScore, useScoreState } from 'src/store/coins/scoreSlice';
import { ICoinsChartData } from 'src/types/coins';
import { AuthContext } from "../../providers/AuthProvider";
import { ChallengeDriverItem } from "../../types/gamification";

interface UseCoinsParams {
  initialSearchValue?: string;
  initialPage?: number;
  initialSortBy?: string;
  initialSortDir?: 'asc' | 'desc';
}

const PAGE_SIZE = 10;

export function useControll({
  initialSearchValue = '',
  initialPage = 1,
  initialSortBy = 'tripDate',
  initialSortDir = 'desc',
}: UseCoinsParams = {}) {
  const dispatch = useDispatch();
  const { coins } = useCoinsState();

  const [searchValue, setSearchValue] = useState(initialSearchValue);
  const [page, setPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(0);
  const [fieldSort, setFieldSort] = useState<string>(initialSortBy);
  const [sort, setSort] = useState<{ [key: string]: 'asc' | 'desc' }>({ [initialSortBy]: initialSortDir });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { totalScore } = useScoreState();
  const [dataChart, setDataChart] = useState<ICoinsChartData[]>([]);
  const [challengeDrivers, setChallengeDrivers] = useState<ChallengeDriverItem[]>([]);
  const { followers, getDetailUser } = useContext(AuthContext);

  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);

  useEffect(() => {
    loadPointsAndClean();
  }, []);

  useEffect(() => {

    if (!startDate && !endDate) {
      loadMorePoints(page);
      return;
    }

    if (!startDate || !endDate) {
      return;
    }

    if (new Date(endDate) < new Date(startDate)) {
      Notification.error('Data final deve ser maior que a inicial');
      return;
    }

    loadMorePoints(page);
  }, [searchValue, sort, fieldSort, startDate, endDate, page]);

  const loadPointsAndClean = async () => {
    try {
      const { result, pages } = await searchPoints(page);

      // Gera dados do gráfico a partir dos dados da tabela
      const chartData = generateChartDataFromTableData(result);

      // Calcula o total de pontos a partir dos dados da tabela
      const calculatedTotalScore = calculateTotalScoreFromTableData(result);

      setDataChart(chartData);
      dispatch(setTotalScore(calculatedTotalScore));
      setChallengeDrivers(result);
      setTotalPages(pages);
    } catch (error) {
      setError("Erro ao carregar pontos");
    }
  }


  const generateChartDataFromTableData = (tableData: any[]): ICoinsChartData[] => {
    // Agrupa por data e soma os pontos
    const dailyScores = new Map<string, number>();

    tableData.forEach(item => {
      if (item.tripDate) {
        const date = new Date(item.tripDate);
        const dateKey = date.toISOString().split('T')[0]; // YYYY-MM-DD
        const currentPoints = dailyScores.get(dateKey) || 0;
        const newPoints = currentPoints + (item.points || 0);
        dailyScores.set(dateKey, newPoints);
      }
    });

    // Converte para o formato esperado pelo gráfico
    const chartData: ICoinsChartData[] = Array.from(dailyScores.entries()).map(([dateKey, points]) => {
      const date = new Date(dateKey);
      return {
        mes: date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
        mesExtenso: date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' }),
        valor: points,
        date: date,
      };
    }).sort((a, b) => a.date.getTime() - b.date.getTime());

    return chartData;
  };

  const calculateTotalScoreFromTableData = (tableData: any[]): number => {
    const total = tableData.reduce((sum, item) => {
      return sum + (item.points || 0);
    }, 0);

    return total;
  };



  const searchPoints = async (page: number) => {
    try {
      setError(null);

      const size = page * PAGE_SIZE;
      const result = await getChallengeDriverPerAccount(0, size, searchValue || undefined, startDate, endDate, fieldSort, sort[fieldSort]);

      const mapped = result.content.map((res) => {
        const amity = getDetailUser?.(res?.profileId);
        const displayName = amity?.metadata?.metadata?.displayName;
        const name = amity?.metadata?.metadata?.givenName;
        const familyName = amity?.metadata?.familyName;
        const isConnected = !!followers?.[res?.driverId]?.userId;
        const challegeName = res?.challenge?.name;
        const driverName = displayName || (name && `${name} ${familyName}`) || res.driverName || "Sem nome";
        const avatar = amity?.metadata?.imageProfile?.fileUrl;

        return {
          ...res,
          driverName,
          avatar,
          isConnected,
          challegeName,
        };
      });
      
      return { result: mapped, pages: result.totalPages };
    } catch (err) {
      setError("Erro ao buscar dados do backend");
      return { result: [], pages: 0 };
    } finally {
      setLoading(false);
    }
  };

  const loadMorePoints = async (newPage?: number) => {
    const { result, pages } = await searchPoints(newPage || page);
    setChallengeDrivers(result);
    setTotalPages(pages);

    // Gera dados do gráfico a partir dos dados da tabela
    const chartData = generateChartDataFromTableData(result);

    // Calcula o total de pontos a partir dos dados da tabela
    const calculatedTotalScore = calculateTotalScoreFromTableData(result);

    setDataChart(chartData);
    dispatch(setTotalScore(calculatedTotalScore));
  }

  const handlePage = (params: { page: number, pages?: number }) => {
    let newPage = params.page || page;
    newPage = newPage == 0 ? 1 : newPage;
    setPage(newPage);
    loadMorePoints(newPage);
  };

  const handleSearchValueChange = async (text: string) => {
    setSearchValue(text);
    setPage(1);
  }

  const handleDownload = () => {
    try {
      if (!challengeDrivers || challengeDrivers.length === 0) {
        Notification.error('Não há dados para download');
        return;
      }

      const headers = ['Motorista', 'Desafio', 'Tipo', 'Destino', 'Data', 'Pontos'];
      const csvData = challengeDrivers.map(item => [
        item.driverName || 'Sem nome',
        item.challegeName || 'N/A',
        'Viagem',
        item.tripDestination || 'N/A',
        item.tripDate ? moment(item.tripDate).format('DD/MM/YYYY') : 'N/A',
        item.points?.toString() || '0'
      ]);

      const csvContent = [
        headers.join(','),
        ...csvData.map(row => row.join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `score-${moment().format('DDMMYYYY')}.csv`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      Notification.success('Download realizado com sucesso');
    } catch (error) {
      Notification.error('Erro ao gerar arquivo de download');
    }
  };

  return {
    loading,
    error,
    totalScore,
    totalPages,
    coins,
    fieldSort,
    sort,
    searchValue,
    page,
    dataChart,
    challengeDrivers,
    PAGE_SIZE,
    startDate,
    endDate,

    setStartDate,
    setEndDate,
    handlePage,
    setFieldSort,
    setSort,
    setSearchValue,
    setPage,
    handleSearchValueChange,
    handleDownload,
  };
}